// Ported from dev-scrips-pm-react services/scheduling/use-prebook-appointment-profile.ts (PROD-1190 calendar port)
import { useMutation } from '@tanstack/react-query';
import { post } from '@/shared/api/api-client';
import type { AppointmentSlot, AppointmentType } from '@/types/scheduling-api';

/**
 * POST /api/v1/Practice/PreBookSelectedAppointmentProfile
 *
 * Mirrors Flutter `fetchPreSelectedApptProfileDetails()` →
 * `scheduling_data_source_impl.dart:912–943`. Fired whenever the user
 * picks (or auto-picks) an appointment profile. The response carries
 * the data needed to auto-populate downstream fields:
 *   - reminderList → first entry with `enableInAppBooking === true`
 *     becomes `appointment.reminderProfile` (required by save rule)
 *   - billingProfiles → drives the billing profile id
 *   - providerReasonForVisits → RFV options for the profile
 *   - slots → seed the time-slot picker
 */

export interface ReminderProfileRule {
  /** Server id for this rule row (Flutter `ReminderProfileType.id`). */
  id?: string;
  /** "InApp" | "SMS" | "Email" | … */
  type?: string;
  /** Flutter ships `number` as a string ("1", "7", "3"). Some payloads
   * also send `duration` as a number — accept either. */
  number?: string;
  duration?: number;
  /** "Hour" | "Day" | "Minute" — backend casing varies. Flutter uses `time`. */
  time?: string;
  unit?: string;
  /** "Before" | "After" — most reminders are "Before". Flutter uses `whenToSend`. */
  whenToSend?: string;
  beforeAfter?: string;
}

export interface ReminderProfile {
  id?: string;
  profileName?: string;
  enableInAppBooking?: boolean;
  isDefault?: boolean;
  customNote?: string;
  reminderProfileType?: ReminderProfileRule[];
}

/**
 * Format a single reminder rule like Flutter's reminder card —
 * "In-App 1 hour before".
 */
export function formatReminderRule(rule: ReminderProfileRule): string {
  const type = (rule.type ?? '').replace(/^In[ -]?App$/i, 'In-App');
  const rawDuration = rule.duration ?? Number(rule.number ?? 0);
  const duration = Number.isFinite(rawDuration) ? rawDuration : 0;
  const unit = (rule.unit ?? rule.time ?? '').toLowerCase();
  const when = (rule.beforeAfter ?? rule.whenToSend ?? 'Before').toLowerCase();
  const unitLabel = duration === 1 ? unit : `${unit}s`;
  return `${type} ${duration} ${unitLabel} ${when}`.replace(/\s+/g, ' ').trim();
}

/** Build the bullet-list strings shown inside the Reminder card. */
export function reminderRules(profile: ReminderProfile | undefined): string[] {
  if (!profile?.reminderProfileType?.length) return [];
  return profile.reminderProfileType.map(formatReminderRule);
}

export interface BillingProfileEntry {
  id?: string;
  name?: string;
  isDefault?: boolean;
  /** Display amount — Flutter `BillingProfile.totalAmount` (line 80). */
  totalAmount?: number;
  /** Pre-discount amount — fallback display. */
  totalCost?: number;
}

/**
 * Wrapper object — Flutter ships `billingProfiles` as an OBJECT containing
 * `{ providerId, billingProfiles: BillingProfile[] }` (note: same key on
 * the wrapper and the inner array). Older payloads may flatten to a bare
 * array; we accept both.
 */
export interface BillingProfilesResponse {
  providerId?: string;
  billingProfiles?: BillingProfileEntry[];
  /** Singular alias seen on some endpoints. */
  billingProfile?: BillingProfileEntry[];
}

export interface ProviderReasonForVisit {
  code: string;
  display: string;
  definition?: string;
}

export interface PreBookAppointmentProfileResponse {
  reminderList?: ReminderProfile[];
  providerReasonForVisits?: ProviderReasonForVisit[];
  appointmentProfileResponseList?: unknown;
  billingProfiles?: BillingProfilesResponse;
  slots?: AppointmentSlot[];
}

interface PreBookProfileParams {
  organizationId: string;
  practiceId: string;
  appointmentProfileId: string;
  providerId: string;
  /** ISO date — the call expands to start/end of day. */
  date: string;
}

function toUtcRange(date: string): { start: string; end: string } {
  const d = new Date(date);
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return {
    start: `${y}-${m}-${day}T00:00:00Z`,
    end: `${y}-${m}-${day}T23:59:59Z`,
  };
}

export function usePreBookAppointmentProfile() {
  return useMutation<PreBookAppointmentProfileResponse, Error, PreBookProfileParams>({
    mutationFn: async (params) => {
      const { start, end } = toUtcRange(params.date);
      const body = {
        organizationId: params.organizationId,
        practiceId: params.practiceId,
        appointmentProfileId: params.appointmentProfileId,
        providerId: params.providerId,
        slotsRequest: {
          practiceId: params.practiceId,
          practitionerId: params.providerId,
          start,
          end,
          appointmentProfileId: params.appointmentProfileId,
          count: 100,
          searchFromUserApp: false,
        },
      };
      return post<PreBookAppointmentProfileResponse>(
        '/api/v1/Practice/PreBookSelectedAppointmentProfile',
        body,
      );
    },
  });
}

/**
 * Auto-pick rule from Flutter `create_appointment_widget.dart:2249–2257`.
 * Picks the first reminder marked `enableInAppBooking`. Falls back to
 * the first reminder in the list (so save can still enable when none
 * are explicitly flagged).
 */
export function pickDefaultReminder(reminders: ReminderProfile[] | undefined): ReminderProfile | undefined {
  if (!reminders || reminders.length === 0) return undefined;
  return reminders.find((r) => r.enableInAppBooking === true) ?? reminders[0];
}

/** Flatten the response wrapper into the inner BillingProfile[] array.
 * Accepts the Flutter `{ providerId, billingProfiles: [] }` wrapper, the
 * `billingProfile` (singular) variant, or a flat array — backends in
 * different endpoints have shipped each shape. */
export function billingProfileList(
  bp: BillingProfilesResponse | BillingProfileEntry[] | undefined | null,
): BillingProfileEntry[] {
  if (!bp) return [];
  if (Array.isArray(bp)) return bp;
  return bp.billingProfiles ?? bp.billingProfile ?? [];
}

/**
 * Resolve the billing profile attached to a given appointment profile.
 * Mirrors Flutter `create_appointment_widget.dart:2258` which reads
 * `appointment.appointmentProfile.billingProfile.totalCost` — the
 * billing profile lives EMBEDDED on the appointment profile itself.
 *
 * Source priority (Flutter parity):
 *   1. `appointmentProfile.billingProfile` (embedded object) — Flutter's
 *      canonical source. The wire ships the full BillingProfile object
 *      ON the appointment profile; the appointment-profile's
 *      billingProfileId references a billing profile that may NOT be
 *      in the broader provider billing-profile list (the appointment-
 *      profile-attached billing profile can be owned by a different
 *      scope, e.g. a global / shared one). Reading the embedded object
 *      directly avoids the list-membership mismatch.
 *   2. `billingProfileList(bp).find(b => b.id === billingProfileId)` —
 *      fallback list lookup. Useful when the wire ships only the id
 *      reference and the broader list happens to contain it.
 *
 * Returns undefined when neither path resolves a billing profile.
 */
export function pickAttachedBillingProfile(
  appointmentProfile:
    | {
        billingProfileId?: string;
        billingProfile?: BillingProfileEntry | null;
      }
    | undefined
    | null,
  bp: BillingProfilesResponse | BillingProfileEntry[] | undefined | null,
): BillingProfileEntry | undefined {
  const embedded = appointmentProfile?.billingProfile;
  const id = embedded?.id ?? appointmentProfile?.billingProfileId;
  if (!id) return undefined;
  const fromList = billingProfileList(bp).find((b) => b.id === id);
  if (embedded && embedded.id) {
    // Embedded is the canonical source — but some endpoints ship a
    // PARTIAL embedded object (id + name only, no totalCost / totalAmount).
    // `formatBillingProfile` would then render "<name> - AED" with a
    // blank cost. If the broader list has a fuller record for the
    // same id, prefer it; otherwise return the partial embedded so the
    // user still sees the name (degrades gracefully). Samer review §3.
    const hasEmbeddedCost =
      (embedded.totalCost != null && Number.isFinite(embedded.totalCost)) ||
      (embedded.totalAmount != null && Number.isFinite(embedded.totalAmount));
    if (hasEmbeddedCost) return embedded;
    return fromList ?? embedded;
  }
  return fromList;
}

/**
 * Format a billing-profile entry exactly like Flutter `BillingProfile.
 * getDisplayText` (provider_billing_settings_model.dart:83-91):
 *   - totalAmount == 0 (or unset) → "<name> - <currency><totalCost>"
 *   - totalAmount  > 0           → "Total - <currency><totalAmount>"
 * No rounding — the dart side does a raw `.toString()` on the double.
 */
export function formatBillingProfile(b: BillingProfileEntry, currency = 'AED'): string {
  const totalAmount = b.totalAmount ?? 0;
  if (totalAmount === 0) {
    return `${b.name ?? ''} - ${currency}${b.totalCost ?? ''}`;
  }
  return `Total - ${currency}${totalAmount}`;
}

// Re-export shared types so callers don't import from two places.
export type { AppointmentSlot, AppointmentType };
